import { inject, Injectable, Type } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ICustomCtaValue, IGlobalCtaValue } from '@campaign-form/campaign-form.interface';
import { EAnalyticsStatus } from '@enums/campaign-analytics.enum';
import { EDeliverableType } from '@enums/campaign.enum';
import { TranslateService } from '@ngx-translate/core';
import { v4 as uuidv4 } from 'uuid';
import { IDeliverableConfig, TDeliverable } from '../../deliverable-list.interface';
import {
  ITextDescriptionDeliverable,
  ITextDescriptionDeliverableForm,
  TTextDescriptionDeliverableType,
} from './text-description-deliverable.interface';

export interface ITextDescriptionServiceConfig {
  component: Type<unknown>;
  defaultNameTranslationKey: string;
  deliverableType: TTextDescriptionDeliverableType;
}

@Injectable({
  providedIn: 'root',
})
export class TextDescriptionDeliverableService {
  private readonly translate = inject(TranslateService);

  public getConfig(config: ITextDescriptionServiceConfig): IDeliverableConfig {
    return {
      component: config.component as IDeliverableConfig['component'],
      createForm: (
        item?: TDeliverable,
        creatorId?: string,
        isViewMode?: boolean,
      ): FormGroup<ITextDescriptionDeliverableForm> | null =>
        this.createTextDescriptionDeliverableForm(config, item, creatorId, isViewMode),
    };
  }

  private createTextDescriptionDeliverableForm(
    config: ITextDescriptionServiceConfig,
    item?: TDeliverable,
    creatorId?: string,
    isViewMode?: boolean,
  ): FormGroup<ITextDescriptionDeliverableForm> | null {
    if (item && item.type !== config.deliverableType) {
      return null;
    }

    const form = this.buildFormGroup(config, this.toTextDescriptionItem(item), creatorId, isViewMode);

    if (isViewMode) {
      form.disable();
    }

    return form;
  }

  private buildFormGroup(
    config: ITextDescriptionServiceConfig,
    textDescriptionItem: ITextDescriptionDeliverable | undefined,
    creatorId: string | undefined,
    isViewMode: boolean | undefined,
  ): FormGroup<ITextDescriptionDeliverableForm> {
    const defaultName = this.translate.instant(config.defaultNameTranslationKey);
    const itemId = textDescriptionItem ? (textDescriptionItem.id ?? uuidv4()) : uuidv4();
    const itemName = textDescriptionItem ? textDescriptionItem.name : defaultName;
    const itemText = textDescriptionItem ? textDescriptionItem.text : null;
    const textValidators = isViewMode ? [] : [Validators.required, Validators.maxLength(4096)];
    const exclusiveTo = textDescriptionItem?.exclusiveTo || creatorId || null;
    const customCtaValues = textDescriptionItem ? (textDescriptionItem.customCtaValues ?? []) : [];

    return new FormGroup<ITextDescriptionDeliverableForm>({
      analyticsCurrentCount: new FormControl<number | null>(textDescriptionItem?.analyticsCurrentCount ?? null),
      analyticsCurrentRatio: new FormControl<number | null>(textDescriptionItem?.analyticsCurrentRatio ?? null),
      customCtaValues: new FormControl<ICustomCtaValue[]>(customCtaValues, { nonNullable: true }),
      exclusiveTo: new FormControl<string | null>(exclusiveTo),
      globalCtaValues: new FormControl<IGlobalCtaValue[] | null>(textDescriptionItem?.globalCtaValues ?? null),
      id: new FormControl<string | null>(itemId, Validators.required),
      isNew: new FormControl<boolean>(!textDescriptionItem?.id),
      name: new FormControl<string | null>(itemName, [Validators.required, Validators.maxLength(255)]),
      sponsoredContentRelevant: new FormControl<boolean>(textDescriptionItem?.sponsoredContentRelevant ?? true, {
        nonNullable: true,
      }),
      status: new FormControl<EAnalyticsStatus | null>(textDescriptionItem?.status ?? null),
      text: new FormControl<string | null>(itemText, textValidators),
      type: new FormControl<TTextDescriptionDeliverableType>(config.deliverableType, { nonNullable: true }),
    });
  }

  private toTextDescriptionItem(item?: TDeliverable): ITextDescriptionDeliverable | undefined {
    if (!item) {
      return undefined;
    }

    if (item.type === EDeliverableType.talkingPointAi) {
      return item as ITextDescriptionDeliverable;
    }

    return undefined;
  }
}
